import { Input, Textarea } from '../../../components/ui/FormField.js';
import { LastChanged } from '../parts/LastChanged.js';
import { SettingCard } from '../parts/parts.js';
import type { ReceiptDetails } from '../settingsTypes.js';
import type { CardProps } from './cardProps.js';

/**
 * 390 (#1310, mock-up screen 5): the business details printed at the top of every customer receipt. Saved with the tab
 * like every card; the platform checks the limits and names the field it refuses.
 */
const EMPTY: ReceiptDetails = { company_name: '', registration_no: '', address: '', sst_no: '' };

const FIELDS: Array<{
  key: keyof ReceiptDetails;
  label: string;
  max: number;
  hint?: string;
  long?: boolean;
}> = [
  { key: 'company_name', label: 'Company name', max: 120 },
  { key: 'registration_no', label: 'Registration no.', max: 40 },
  { key: 'address', label: 'Address', max: 300, long: true },
  {
    key: 'sst_no',
    label: 'SST no. (optional)',
    max: 40,
    hint: 'Printed only on receipts where SST was charged.',
  },
];

export function ReceiptDetailsCard({ draft, latest, refusal, refusals }: CardProps) {
  const value = draft.value('receipt_details') ?? EMPTY;
  const reasons = refusals?.('receipt_details') ?? [];
  const reasonFor = (k: string) =>
    reasons.find((r) => r.field === `receipt_details.${k}`)?.message ?? null;
  return (
    <SettingCard
      testId="receipt-details-card"
      title="Receipt details"
      dirty={draft.isDirty('receipt_details')}
      refusal={reasons.length === 0 ? refusal('receipt_details') : null}
      description={
        <p>
          Printed at the top of every customer receipt. Empty: receipts show "CARii Kereta" only.
        </p>
      }
      footer={<LastChanged setting="receipt_details" latest={latest} />}
    >
      <div className="flex flex-col gap-3">
        {FIELDS.map((f) => {
          const id = `receipt-details-${f.key}`;
          const reason = reasonFor(f.key);
          const onChange = (v: string) => draft.set('receipt_details', { ...value, [f.key]: v });
          return (
            <div
              key={f.key}
              className="grid gap-1 sm:grid-cols-[170px_minmax(0,1fr)] sm:items-start sm:gap-3"
            >
              <label htmlFor={id} className="pt-2 text-sm text-steel">
                {f.label}
              </label>
              <div>
                {f.long ? (
                  <Textarea
                    id={id}
                    rows={2}
                    value={value[f.key]}
                    onChange={(e) => onChange(e.target.value)}
                  />
                ) : (
                  <Input
                    id={id}
                    value={value[f.key]}
                    placeholder={f.key === 'sst_no' ? 'Not registered' : undefined}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
                {f.key === 'company_name' && (
                  <p className="mt-1 text-xs text-steel">{`${value.company_name.trim().length} / ${f.max}`}</p>
                )}
                {f.hint && <p className="mt-1 text-xs text-steel">{f.hint}</p>}
                {reason && (
                  <p
                    data-testid={`receipt-details-error-${f.key}`}
                    className="mt-1 text-xs text-semantic-danger-text"
                  >
                    {reason}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </SettingCard>
  );
}
